import React from 'react';
import PropTypes from 'prop-types';
import { Chip } from '@mui/material';
import { SESSION_STATUS_META, attendanceMetaFromStatus } from '../utils/session-labels';

/**
 * Visual indicator for either a session status (pending|in_progress|completed)
 * or an attendance status (present|absent|justified|null).
 *
 * Two render flavors via the `variant` prop:
 *   - 'session'    (default) for session lifecycle states
 *   - 'attendance' for per-employee attendance state
 */
const styles = {
  chip: {
    borderRadius: 999,
    height: 24,
    fontSize: 12,
    fontWeight: 600,
    px: 0.5,
    '& .MuiChip-label': { px: 1 },
  },
  dot: size => ({
    display: 'inline-block',
    width: size,
    height: size,
    borderRadius: '50%',
    marginRight: 6,
  }),
};

export default function SessionStatusPill({ status, variant, size }) {
  const meta =
    variant === 'attendance'
      ? attendanceMetaFromStatus(status)
      : (SESSION_STATUS_META[status] ?? SESSION_STATUS_META.pending);
  const dotSize = size === 'sm' ? 6 : 8;

  return (
    <Chip
      label={
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <span style={{ ...styles.dot(dotSize), background: meta.color }} />
          {meta.label}
        </span>
      }
      sx={{
        ...styles.chip,
        background: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.color}33`,
        height: size === 'sm' ? 20 : 24,
        fontSize: size === 'sm' ? 11 : 12,
      }}
    />
  );
}

SessionStatusPill.propTypes = {
  status: PropTypes.oneOfType([
    PropTypes.oneOf(['pending', 'in_progress', 'completed', 'present', 'absent', 'justified']),
    PropTypes.oneOf([null]),
  ]),
  variant: PropTypes.oneOf(['session', 'attendance']),
  size: PropTypes.oneOf(['sm', 'md']),
};

SessionStatusPill.defaultProps = {
  status: 'pending',
  variant: 'session',
  size: 'md',
};
